import { Trans, useLingui } from "@lingui/react/macro";
import type { ConnectionField } from "@rakazo/contracts";
import type { ConnectionFieldsPrompt } from "@rakazo/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
} from "@rakazo/ui-web";
import type { FormEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

type Request = Parameters<ConnectionFieldsPrompt>[0] & {
  resolve: (values: Record<string, string> | null) => void;
};

/**
 * `askFields` asks for the details an app's sign-in needs before it opens; render `fieldsDialog`.
 */
export function useConnectionFieldsPrompt() {
  const [request, setRequest] = useState<Request | null>(null);
  const pending = useRef<Request | null>(null);
  const settle = useCallback((values: Record<string, string> | null) => {
    pending.current?.resolve(values);
    pending.current = null;
    setRequest(null);
  }, []);
  useEffect(() => () => pending.current?.resolve(null), []);
  const askFields = useCallback<ConnectionFieldsPrompt>(
    (next) =>
      new Promise((resolve) => {
        pending.current?.resolve(null);
        const entry = { ...next, resolve };
        pending.current = entry;
        setRequest(entry);
      }),
    [],
  );
  return {
    askFields,
    fieldsDialog: request ? (
      <ConnectionFieldsDialog
        key={`${request.app}:${request.invalid.join(",")}`}
        request={request}
        onDone={settle}
      />
    ) : null,
  };
}

function ConnectionFieldsDialog({
  request,
  onDone,
}: {
  request: Request;
  onDone: (values: Record<string, string> | null) => void;
}) {
  const { t } = useLingui();
  const [values, setValues] = useState<Record<string, string>>(request.values);
  const app = request.app;
  const ready = request.fields.every((field) => values[field.name]?.trim());
  const label = (field: ConnectionField) =>
    field.kind === "store_address" ? t`Store address` : field.label;
  function submit(event: FormEvent) {
    event.preventDefault();
    if (ready) onDone(values);
  }
  return (
    <Dialog open onOpenChange={(open) => !open && onDone(null)}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t`Connect ${app}`}</DialogTitle>
          </DialogHeader>
          {request.fields.map((field, index) => {
            const invalid = request.invalid.includes(field.name);
            return (
              <Field key={field.name} data-invalid={invalid || undefined}>
                <FieldLabel htmlFor={`connection-field-${field.name}`}>{label(field)}</FieldLabel>
                <Input
                  id={`connection-field-${field.name}`}
                  autoFocus={index === 0}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-invalid={invalid || undefined}
                  placeholder={
                    field.kind === "store_address" ? "yourstore.myshopify.com" : undefined
                  }
                  value={values[field.name] ?? ""}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [field.name]: event.target.value }))
                  }
                />
                {invalid ? <FieldError>{t`Check this and try again.`}</FieldError> : null}
              </Field>
            );
          })}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onDone(null)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={!ready}>
              <Trans>Continue</Trans>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
